import React, { useRef, useEffect } from "react";
import { useKeenSlider } from "keen-slider/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Button from "../../../../components/ui/Button/Button";
import { testimonialsData } from "../../../../data/testimonials";
import "keen-slider/keen-slider.min.css";
import "./Testimonials.css";

gsap.registerPlugin(ScrollTrigger);

const getInitials = (name) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const Testimonials = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const sliderWrapRef = useRef(null);
  const [currentSlide, setCurrentSlide] = React.useState(0);

  const [sliderRef, instanceRef] = useKeenSlider(
    {
      loop: true,
      mode: "snap",
      slides: {
        perView: 3,
        spacing: 28,
      },
      slideChanged(s) {
        setCurrentSlide(s.track.details.rel);
      },
      breakpoints: {
        "(max-width: 1100px)": {
          slides: { perView: 2, spacing: 24 },
        },
        "(max-width: 768px)": {
          slides: { perView: 1, spacing: 20 },
        },
      },
    },
    [
      (slider) => {
        let timeout;
        let mouseOver = false;

        const clearNextTimeout = () => clearTimeout(timeout);
        const nextTimeout = () => {
          clearTimeout(timeout);
          if (mouseOver) return;
          timeout = setTimeout(() => slider.next(), 4500);
        };

        slider.on("created", () => {
          slider.container.addEventListener("mouseover", () => {
            mouseOver = true;
            clearNextTimeout();
          });
          slider.container.addEventListener("mouseout", () => {
            mouseOver = false;
            nextTimeout();
          });
          nextTimeout();
        });
        slider.on("dragStarted", clearNextTimeout);
        slider.on("animationEnded", nextTimeout);
        slider.on("updated", nextTimeout);
      },
    ],
  );

  useEffect(() => {
    const ctx = gsap.context(() => {
      const headerEls = headerRef.current.querySelectorAll(".animate-in");

      gsap.fromTo(
        headerEls,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );

      gsap.fromTo(
        sliderWrapRef.current,
        { opacity: 0, y: 60 },
        {
          opacity: 1,
          y: 0,
          duration: 0.9,
          ease: "power3.out",
          delay: 0.25,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const total = testimonialsData.testimonials.length;

  return (
    <section className="testimonials" ref={sectionRef}>
      <div className="testimonials__container">
        <div className="testimonials__header" ref={headerRef}>
          <div className="testimonials__header-left">
            <div className="testimonials__badge animate-in">
              {testimonialsData.badge}
            </div>
            <h2 className="testimonials__headline animate-in">
              {testimonialsData.headline}
            </h2>
            <p className="testimonials__description animate-in">
              {testimonialsData.description}
            </p>
          </div>
        </div>

        <div className="testimonials__slider-wrap" ref={sliderWrapRef}>
          <div ref={sliderRef} className="keen-slider testimonials__track">
            {testimonialsData.testimonials.map((testimonial, index) => (
              <div key={index} className="keen-slider__slide testimonial-slide">
                <div className="testimonial-card">
                  <div className="testimonial-card__stars">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i}>★</span>
                    ))}
                  </div>
                  <p className="testimonial-card__text">{testimonial.text}</p>
                  <div className="testimonial-card__author">
                    <div className="testimonial-card__avatar">
                      {getInitials(testimonial.name)}
                    </div>
                    <div>
                      <div className="testimonial-card__name">
                        {testimonial.name}
                      </div>
                      <div className="testimonial-card__position">
                        {testimonial.position}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="testimonials__footer">
            <div className="testimonials__dots">
              {[...Array(total)].map((_, i) => (
                <button
                  key={i}
                  className={`testimonials__dot ${i === currentSlide ? "testimonials__dot--active" : ""}`}
                  onClick={() => instanceRef.current?.moveToIdx(i)}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
